/* rc-animations.js — Roughcut
   ══════════════════════════════════════════════════════════════════════════
   THE ONE SCROLL-REVEAL OBSERVER FOR THE WHOLE SITE

   How this got the way it was, because it explains the shape of the fix.

   Sections were built over months, each one arriving with its own reveal
   code copy-pasted from the last and renamed. By the time anyone counted
   there were about twenty IntersectionObservers running on this site and
   SIX different vocabularies for the same idea:

     .rc-reveal          → add 'visible'      rc-animations.js (this file)
     .rc-reveal-stagger  → add 'visible'      "  , delayed per sibling
     .reveal             → add 'visible'      rc-about-page, rc-help-about
     .ww-reveal          → add 'is-visible'   14 copies, one per workwear section
     .rc-shl-reveal      → add 'in'           rc-start-here-landing
     .rc-anim            → add 'rc-anim--in'  rc-community.js
     [data-rv]           → add 'in'           rc-home.js, rc-product.js
     [data-rv]           → add 'rcp-in'       rc-patterns.js   ← same hook, different class

   That last pair is the one to look at. The same attribute means two
   different class names depending on which page you're on. Nothing was
   broken by it, but it's a trap: anyone touching one file breaks the other,
   and nobody would guess.

   THE FIX IS NOT A REWRITE. Rewriting 38 sections to one vocabulary is a big
   diff across every page of a live shop, and the payoff is tidiness. Instead
   this file learns all six vocabularies and adds every shown-class an element
   could want. That works because the classes are inert where they aren't
   used — a grep for bare `.in`, `.on`, `.visible` and `.is-visible` selectors
   across every stylesheet and every inline <style> in the theme returns zero.
   The only rules that reference them are qualified, like `.rc-shl-reveal.in`.
   So putting 'in' on an element that has never heard of it changes nothing.

   The per-section observers stay where they are and keep working. Adding a
   class twice is a no-op, so the two systems sit on top of each other without
   arguing. What this file buys is that every reveal on the site now fires at
   the same threshold, with the same timing, and — the point of the exercise —
   that a section whose own observer never ran still reveals.

   ES5 on purpose. The asset pipeline on this theme has rejected const, arrow
   functions and template literals before now.
   ══════════════════════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* Runs before DOMContentLoaded, deliberately. `no-js` is the outermost
     fail-safe in rc-tokens.css: while it's on <html>, every reveal class on
     the site is forced visible. The moment this line executes we've proven
     JavaScript works, so it's safe to let things hide themselves. */
  var html = document.documentElement;
  html.classList.remove('no-js');
  html.classList.add('js-loaded');

  /* ─── the six vocabularies ───────────────────────────────────────────────
     selector → which classes mean "you're on screen now".
     Where a hook is ambiguous we add every candidate; see the note above on
     why that's safe. */
  var LANES = [
    { sel: '.rc-reveal',        add: ['visible'] },
    { sel: '.rc-reveal-quote',  add: ['visible'] },
    { sel: '.rc-img-reveal',    add: ['visible'] },
    { sel: '.reveal',           add: ['visible', 'is-visible'] },
    { sel: '.reveal-left',      add: ['visible', 'is-visible'] },
    { sel: '.reveal-right',     add: ['visible', 'is-visible'] },
    { sel: '.reveal-scale',     add: ['visible', 'is-visible'] },
    { sel: '.ww-reveal',        add: ['is-visible'] },
    { sel: '.rc-shl-reveal',    add: ['in'] },
    { sel: '.rc-anim',          add: ['rc-anim--in'] },
    { sel: '.rc-anim-group',    add: ['rc-anim--in'] },
    { sel: '[data-rv]',         add: ['in', 'rcp-in'] }
  ];

  var ALL = LANES.map(function (l) { return l.sel; }).join(',');

  function addClasses(el, list) {
    for (var i = 0; i < list.length; i++) el.classList.add(list[i]);
  }

  /* An element can sit in more than one lane (.rc-reveal AND [data-rv], say),
     so union the classes rather than stopping at the first match. */
  function reveal(el) {
    if (el.getAttribute('data-rc-shown') === '1') return;
    el.setAttribute('data-rc-shown', '1');
    for (var i = 0; i < LANES.length; i++) {
      try {
        if (el.matches(LANES[i].sel)) addClasses(el, LANES[i].add);
      } catch (e) { /* a selector this browser can't parse shouldn't stop the rest */ }
    }
    staggerChildren(el);
  }

  /* Children of a revealed parent come in as a run rather than all at once.
     The gap is read from --rc-stagger so it's tuned in one place with
     everything else, and falls back to 70ms if the token sheet didn't load. */
  function staggerStep() {
    var v = 0;
    try {
      v = parseFloat(getComputedStyle(html).getPropertyValue('--rc-stagger'));
    } catch (e) {}
    return (v > 0 && v < 400) ? v : 70;
  }

  function staggerChildren(parent) {
    var kids = parent.querySelectorAll('.rc-reveal-stagger, .rc-anim-child');
    if (!kids.length) return;
    var step = staggerStep();
    for (var i = 0; i < kids.length; i++) {
      (function (el, n) {
        setTimeout(function () { el.classList.add('visible'); }, n * step);
      })(kids[i], i);
    }
  }

  function revealEverything() {
    var els = document.querySelectorAll(ALL + ',.rc-reveal-stagger,.rc-anim-child');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      el.setAttribute('data-rc-shown', '1');
      el.classList.add('visible', 'is-visible', 'in', 'rcp-in', 'rc-anim--in');
    }
  }

  function initScrollReveal() {
    /* Asked their device to stop moving things — vestibular disorder,
       migraine, motion sickness. rc-motion-off is the hook rc-tokens.css uses
       to force everything visible with no transition at all. */
    var reduced = false;
    try {
      reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (e) {}
    if (reduced) {
      html.classList.add('rc-motion-off');
      revealEverything();
      return;
    }

    /* No IntersectionObserver means no way to know what's on screen. Show
       everything immediately — a page with no animation beats a page with no
       content. */
    if (!('IntersectionObserver' in window)) {
      html.classList.add('rc-motion-off');
      revealEverything();
      return;
    }

    var targets = document.querySelectorAll(ALL);
    if (!targets.length) return;

    /* Product cards get a delay by their position in the grid so a row
       arrives as a sweep rather than a slab. */
    var cells = document.querySelectorAll('.rc-prod-cell');
    var step = staggerStep();
    for (var c = 0; c < cells.length; c++) {
      var sibs = cells[c].parentElement ? cells[c].parentElement.children : [];
      var idx = Array.prototype.indexOf.call(sibs, cells[c]);
      if (idx > 0) cells[c].style.transitionDelay = (idx * step) + 'ms';
    }

    var io = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (entries[i].isIntersecting) {
          reveal(entries[i].target);
          io.unobserve(entries[i].target);
        }
      }
    }, {
      /* 8% visible, minus a 40px lip at the bottom. The lip stops an element
         counting as "seen" while it's still a sliver at the edge of the
         screen, which on a phone is most of the time. */
      threshold: 0.08,
      rootMargin: '0px 0px -40px 0px'
    });

    for (var t = 0; t < targets.length; t++) io.observe(targets[t]);

    /* THE WATCHDOG. Third and last fail-safe.

       Everything above assumes this file keeps running. If something throws
       between here and the observer firing — a section script blowing up, an
       extension, a browser quirk — the elements stay at opacity:0 and the
       customer gets blank panels where the copy should be. No error they'd
       notice; the page just looks empty.

       So: two and a half seconds after load, anything still unrevealed and
       already on screen gets shown regardless. If the observer is working
       this finds nothing and costs one pass over the DOM. If it isn't, this
       is the difference between a page and an empty page. */
    setTimeout(function () {
      var stuck = document.querySelectorAll(ALL);
      for (var i = 0; i < stuck.length; i++) {
        if (stuck[i].getAttribute('data-rc-shown') === '1') continue;
        var r = stuck[i].getBoundingClientRect();
        if (r.top < (window.innerHeight || 0) && r.bottom > 0) reveal(stuck[i]);
      }
    }, 2500);
  }

  /* ─── images ────────────────────────────────────────────────────────────
     Lazy images fade rather than snap in. Cached images report complete
     before we ever attach a listener, hence the branch. */
  function initLazyImages() {
    var imgs = document.querySelectorAll('img[loading="lazy"]');
    for (var i = 0; i < imgs.length; i++) {
      (function (img) {
        /* Skip anything already handled. Without this, a second pass puts
           rc-img-loading back onto an image that has already loaded — and
           since that class is what holds an image at opacity 0, the image
           disappears. Costs one class check; prevents a blank grid. */
        if (img.classList.contains('rc-img-loaded')) return;
        if (img.complete) {
          requestAnimationFrame(function () {
            img.classList.remove('rc-img-loading');
            img.classList.add('rc-img-loaded');
          });
        } else {
          img.classList.add('rc-img-loading');
          img.addEventListener('load', function () {
            requestAnimationFrame(function () {
              img.classList.add('rc-img-loaded');
              img.classList.remove('rc-img-loading');
            });
          }, { once: true });
          /* A broken image must not sit at opacity 0 forever. */
          img.addEventListener('error', function () {
            img.classList.remove('rc-img-loading');
            img.classList.add('rc-img-loaded');
          }, { once: true });
        }
      })(imgs[i]);
    }
  }

  function initHeroAnimations() {
    setTimeout(function () { document.body.classList.add('rc-hero-loaded'); }, 100);
  }

  /* ─── YouTube modal ─────────────────────────────────────────────────── */
  function initYTModal() {
    window.rcOpenYT = function (videoId, label) {
      var modal = document.getElementById('rc-yt-modal');
      var frame = document.getElementById('rc-yt-frame');
      var lbl = document.getElementById('rc-yt-label');
      if (!modal || !frame) return;
      if (videoId && videoId.replace(/\s/g, '')) {
        frame.src = 'https://www.youtube.com/embed/' + videoId + '?autoplay=1&rel=0';
      }
      if (lbl) lbl.textContent = label || '';
      modal.classList.add('open');
      document.body.style.overflow = 'hidden';
    };

    window.rcCloseYT = function () {
      var modal = document.getElementById('rc-yt-modal');
      var frame = document.getElementById('rc-yt-frame');
      if (!modal || !frame) return;
      frame.src = '';
      modal.classList.remove('open');
      document.body.style.overflow = '';
    };

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && window.rcCloseYT) window.rcCloseYT();
    });
  }

  /* ─── press feedback ────────────────────────────────────────────────────
     Delegated, so buttons rendered later are covered without rebinding.
     Inline transform rather than a class: it has to beat whatever transform
     the section already put on :hover, and inline always wins. */
  function initButtonPress() {
    var SEL = '.rc-btn, .rc-filter-btn, .rc-email-btn';
    function press(e) {
      var b = e.target.closest ? e.target.closest(SEL) : null;
      if (b) b.style.transform = 'scale(0.97)';
    }
    function release(e) {
      var b = e.target.closest ? e.target.closest(SEL) : null;
      if (b) b.style.transform = '';
    }
    document.addEventListener('pointerdown', press);
    document.addEventListener('pointerup', release);
    document.addEventListener('pointercancel', release);
  }

  /* Each init is isolated. Before this, one throw took the rest of the file
     with it — and the rest of the file includes the thing that makes content
     visible. */
  var booted = false;
  function boot() {
    /* Runs once per page, no matter how many times it's invited.

       Two things invite it twice. Shopify's theme editor re-renders a section
       and re-runs its scripts every time Jack changes a setting, and a
       document can emit DOMContentLoaded after this file has already decided
       the document was ready. Without this guard the second pass re-binds
       every delegated listener — so one tap fires the press handler twice —
       and walks the images again. */
    if (booted) return;
    booted = true;

    var jobs = [initHeroAnimations, initScrollReveal, initLazyImages, initYTModal, initButtonPress];
    for (var i = 0; i < jobs.length; i++) {
      try { jobs[i](); } catch (e) {
        if (window.console && console.warn) console.warn('[rc-animations]', e);
        /* If the reveal step is the one that died, nothing else will show the
           content, so do it here and now. */
        if (jobs[i] === initScrollReveal) { try { revealEverything(); } catch (e2) {} }
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
